<template>
  <v-container fluid>
    <page-title-area>
      <h1>{{ $t('title') }}</h1>
    </page-title-area>

    <!-- コントロール -->
    <v-card class="mb-5" outlined>
      <v-card-text class="d-flex align-center flex-wrap" style="gap: 16px">
        <div class="control-group">
          <span class="control-group__label">{{ $t('label.interval') }}</span>
          <v-btn-toggle v-model="slotMinutes" mandatory dense color="primary">
            <v-btn :value="SLOT_MINUTES.FIFTEEN" small>15{{ $t('unit.min') }}</v-btn>
            <v-btn :value="SLOT_MINUTES.THIRTY" small>30{{ $t('unit.min') }}</v-btn>
            <v-btn :value="SLOT_MINUTES.SIXTY" small>1{{ $t('unit.hour') }}</v-btn>
          </v-btn-toggle>
        </div>

        <div class="control-group">
          <span class="control-group__label">{{ $t('label.filter') }}</span>
          <v-btn-toggle v-model="filterMode" mandatory dense color="primary">
            <v-btn value="exclude_misdetection" small>{{ $t('filter.exclude_misdetection') }}</v-btn>
            <v-btn value="pickup_only" small>{{ $t('filter.pickup_only') }}</v-btn>
            <v-btn value="all" small>{{ $t('filter.all') }}</v-btn>
          </v-btn-toggle>
        </div>

        <div class="control-group">
          <span class="control-group__label">{{ $t('label.date') }}</span>
          <date-picker-dropdown
            type="date"
            :value="selectedDate"
            @input="selectedDate = $event"
            :max="today"
          >
            <template #activator="{ on, attrs, displayValue }">
              <v-btn outlined small v-on="on" v-bind="attrs" class="date-btn">
                <v-icon left>fa-calendar</v-icon>
                {{ displayValue }}
              </v-btn>
            </template>
          </date-picker-dropdown>
        </div>
      </v-card-text>
    </v-card>

    <!-- 空状態 -->
    <v-alert v-if="!loading && placeSensors.length === 0" type="info" outlined>
      {{ $mpt($t('msg.no_sensors')) }}
    </v-alert>

    <!-- ヒートマップ -->
    <v-card v-else outlined>
      <v-card-text ref="heatmapContainer" style="position: relative">
        <v-overlay :value="loading" absolute>
          <page-load-spinner />
        </v-overlay>

        <div class="timeline-scroll">
          <template v-for="(section, index) in sensorSections">
            <div
              :key="`header-${section.type}`"
              class="section-header d-flex align-center text-nowrap"
              :class="{ 'mt-8': index > 0 }"
              data-testid="sensor-section-header"
            >
              {{ section.label }}
              <v-tooltip v-if="section.type === SENSOR_TYPES.RAT_SENSE" bottom max-width="300">
                <template #activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1"
                    :aria-label="ratSenseNotice"
                    data-testid="rat-sense-notice-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span class="rat-sense-notice">{{ ratSenseNotice }}</span>
              </v-tooltip>
            </div>
            <timeline-heatmap
              :key="`heatmap-${section.type}`"
              :sensors="section.sensors"
              :minutes="slotMinutes"
              :events="filteredEvents"
              :clickable="hasMedia(section.type)"
              :show-legend="index === sensorSections.length - 1"
              @cell-hover="onCellHover"
              @cell-leave="onCellLeave"
            />
          </template>
        </div>

        <timeline-popup
          :visible="popup.visible"
          :event="popup.event"
          :sensor-name="popup.sensorName"
          :time-label="popup.timeLabel"
          :count="popup.count"
          :position="popup.position"
          :activity-link="activityLink"
          :detailed="popup.detailed"
          @popup-enter="onPopupEnter"
          @popup-leave="onPopupLeave"
          @show-gallery="showGallery"
        />

        <gallery-view
          v-model="galleryVisible"
          :displayData="galleryData"
          :requestParams="galleryRequestParams"
          @eventUpdated="onEventUpdated"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<i18n lang="yaml">
ja:
  title: 'R型タイムライン'
  label:
    interval: '集計間隔'
    filter: '検出種別'
    date: '日付'
  unit:
    min: '分'
    hour: '時間'
  filter:
    all: 'すべて'
    exclude_misdetection: '誤検出を除外'
    pickup_only: 'ピックアップのみ'
  msg:
    no_sensors: 'この設置場所にセンサーが登録されていません'
    load_error: 'データの読み込みに失敗しました'
    fetch_events_error: 'イベントの取得に失敗しました'
    detection_fetch_failed: '一部のセンサーの検出結果を取得できませんでした'
    rat_sense_notice: |-
      検出結果は前日分までの表示です。
      ピックアップ・誤検出の印を付けられないため、「ピックアップのみ」では表示されません。
en:
  title: 'R-Type Timeline'
  label:
    interval: 'Interval'
    filter: 'Detection'
    date: 'Date'
  unit:
    min: 'min'
    hour: 'hr'
  filter:
    all: 'All'
    exclude_misdetection: 'Exclude misdetections'
    pickup_only: 'Pickup only'
  msg:
    no_sensors: 'No sensors assigned to this place'
    load_error: 'Failed to load data'
    fetch_events_error: 'Failed to fetch events'
    detection_fetch_failed: 'Failed to fetch detections for some sensors'
    rat_sense_notice: |-
      Detections are shown up to the previous day.
      They cannot be marked as pickup or misdetection, so "Pickup only" hides them.
</i18n>

<script>
import { addDays, addMinutes, endOfDay, format, getUnixTime, parse } from 'date-fns';
import DatePickerDropdown from '@/components/atoms/DatePickerDropdown';
import PageLoadSpinner from '@/components/atoms/PageLoadSpinner';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import GalleryView from '@/components/GalleryView/GalleryView';
import AbortControl from '@/libs/AbortControl';
import { fetchAllPages } from '@/libs/fetchAllPages';
import { buildSensorSections, flattenSections } from '@/libs/sensorSections';
import Notifications from '@/mixins/notifications';
import SensorTypes, { SENSOR_TYPES } from '@/mixins/sensorTypes';
import { SLOT_MINUTES } from './timeSlotAggregator';
import TimelineHeatmap from './TimelineHeatmap';
import TimelinePopup from './TimelinePopup';

export default {
  name: 'rat-timeline',
  inject: ['api'],
  mixins: [Notifications, SensorTypes],
  components: { TimelineHeatmap, TimelinePopup, DatePickerDropdown, PageTitleArea, PageLoadSpinner, GalleryView },
  props: {
    placeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedDate: format(new Date(), 'yyyy-MM-dd'),
      slotMinutes: SLOT_MINUTES.FIFTEEN,
      filterMode: 'exclude_misdetection',
      loading: false,
      events: [],
      sensors: [],
      placeEntityIds: [],
      popup: {
        visible: false,
        event: null,
        events: [],
        sensorId: '',
        sensorName: '',
        timeLabel: '',
        count: 0,
        position: { top: 0, left: 0 },
        detailed: true,
      },
      popupCloseTimer: null,
      abortControl: new AbortControl(),
      galleryData: {
        events: [],
        sensor: null,
        index: 0,
      },
      galleryVisible: false,
      gallerySlotRange: null,
    };
  },
  computed: {
    SLOT_MINUTES() { return SLOT_MINUTES; },
    today() {
      return format(new Date(), 'yyyy-MM-dd');
    },
    sensorSections() {
      return buildSensorSections(this.sensors, {
        entityIds: this.placeEntityIds,
        typeNames: this.SENSOR_TYPE_NAMES,
      });
    },
    placeSensors() {
      return flattenSections(this.sensorSections);
    },
    ratSenseNotice() {
      return this.$t('msg.rat_sense_notice');
    },
    filteredEvents() {
      return this.events.filter((ev) => {
        if (!ev.detected) return false;
        if (this.filterMode === 'exclude_misdetection' && ev.misdetection) return false;
        if (this.filterMode === 'pickup_only' && !ev.pickup) return false;
        return true;
      });
    },
    activityLink() {
      const query = {
        timePeriod: JSON.stringify({
          from: this.selectedDate,
          to: this.selectedDate,
        }),
      };
      if (this.popup.sensorId) {
        query.sensorId = this.popup.sensorId;
      }
      return { path: '/activities', query };
    },
    galleryRequestParams() {
      const range = this.gallerySlotRange || this.buildDateRange();
      const params = { from: range.from, to: range.to };
      if (this.filterMode === 'exclude_misdetection') {
        params.misdetection = '0';
      } else if (this.filterMode === 'pickup_only') {
        params.pickup = '1';
      }
      return params;
    },
  },
  methods: {
    /** 表示日の期間を半開区間 [from, to) の unix 秒で返す。イベント取得 API はこの解釈で絞り込む */
    buildDateRange() {
      const day = parse(this.selectedDate, 'yyyy-MM-dd', new Date());
      return {
        from: getUnixTime(day),
        to: getUnixTime(addDays(day, 1)),
      };
    },

    /**
     * 表示日の期間を閉区間 [from, to] の unix 秒で返す。RatSense は from / to が指す日を両端とも含む。
     * 期間は端末のローカル時刻で組み立てるが、RatSense の検索条件はサーバー側で日本時間の日付へ丸められる。
     * 日本時間以外に設定された端末では日の境界がずれるため、利用する端末が日本時間であることを前提とする。
     */
    buildDateRangeClosed() {
      const day = parse(this.selectedDate, 'yyyy-MM-dd', new Date());
      return {
        from: getUnixTime(day),
        to: getUnixTime(endOfDay(day)),
      };
    },
    async fetchEvents() {
      this.abortControl.newSignal();

      this.loading = true;
      try {
        const { allEvents, failedSensors } = await this.loadSensorEvents();
        this.events = allEvents;

        // 取得失敗の通知は 1 度にまとめる。
        if (failedSensors.length > 0) {
          this.notifyError('msg.detection_fetch_failed');
        }
      } catch (error) {
        if (AbortControl.isAbortError(error)) return;
        this.notifyError('msg.fetch_events_error');
      } finally {
        this.loading = false;
      }
    },
    async loadSensorEvents() {
      const { signal } = this.abortControl;
      // どのセンサーの結果かを添字ではなく実体で持ち回る
      const promises = this.placeSensors.map(sensor => this.fetchDetections(sensor, signal)
        .then(events => ({ sensor, events }))
        .catch(error => ({ sensor, error })));
      const results = await Promise.all(promises);
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      return this.collectEvents(results);
    },
    async fetchDetections(sensor, signal) {
      if (sensor.type === SENSOR_TYPES.RAT_SENSE) {
        // RatSense は 1 回の応答で全件返るため、ページ送りの仕組みを通さない
        const period = this.buildDateRangeClosed();
        const response = await this.api.fetchRatSenseDetections(sensor.id, period, { signal });
        return response.events || [];
      }
      return this.fetchAllSensorEvents(sensor.id, this.buildDateRange(), signal);
    },
    async fetchAllSensorEvents(sensorId, params, signal) {
      return fetchAllPages(
        pageParams => this.api.fetchSensorEvents(sensorId, pageParams, { signal }),
        params,
        { signal },
      );
    },
    collectEvents(results) {
      const allEvents = [];
      const failedSensors = [];
      results.forEach(({ sensor, events, error }) => {
        if (error) {
          // 表示の切り替えで打ち切った取得は意図的な中止のため、失敗として数えない
          if (!AbortControl.isAbortError(error)) failedSensors.push(sensor);
          return;
        }
        // 応答の camera_id は種別ごとに作られ方が違うため、要求元のセンサー ID で揃える
        events.forEach((event) => {
          allEvents.push({ ...event, camera_id: sensor.id });
        });
      });
      return { allEvents, failedSensors };
    },
    /**
     * その種別がメディア（映像・画像）の実体を持つかを返す。
     * 持たない種別ではマス目に押せる見た目を付けず、吹き出しも
     * サムネイルとリンクを省いた簡易表示にする。
     * 未知の種別は従来どおりメディアを持つものとして扱う。
     */
    hasMedia(sensorType) {
      return this.SENSOR_DEFINITIONS[sensorType]?.capabilities?.hasMedia ?? true;
    },
    onCellHover({ sensorId, timeLabel, events: slotEvents, rect }) {
      clearTimeout(this.popupCloseTimer);
      if (!slotEvents || slotEvents.length === 0) return;

      const sensor = this.placeSensors.find(s => s.id === sensorId);
      const containerRect = this.$refs.heatmapContainer.getBoundingClientRect();

      this.popup = {
        visible: true,
        event: slotEvents[0],
        events: slotEvents,
        sensorId,
        sensorName: sensor?.name || '',
        timeLabel,
        count: slotEvents.length,
        position: {
          top: rect.bottom - containerRect.top + 4,
          left: rect.left - containerRect.left,
        },
        // 映像を持たない種別の検出結果では、サムネイルとリンクは出さない
        detailed: this.hasMedia(sensor?.type),
      };
    },
    onCellLeave() {
      this.popupCloseTimer = setTimeout(() => {
        this.popup.visible = false;
      }, 200);
    },
    onPopupEnter() {
      clearTimeout(this.popupCloseTimer);
    },
    onPopupLeave() {
      this.popup.visible = false;
    },
    showGallery() {
      const sensor = this.placeSensors.find(s => s.id === this.popup.sensorId);
      const slotStart = parse(
        `${this.selectedDate} ${this.popup.timeLabel}`,
        'yyyy-MM-dd HH:mm',
        new Date(),
      );
      this.gallerySlotRange = {
        from: getUnixTime(slotStart),
        to: getUnixTime(addMinutes(slotStart, this.slotMinutes)),
      };
      this.galleryData = {
        sensor,
        events: this.popup.events,
        index: 0,
      };
      this.popup.visible = false;
      this.galleryVisible = true;
    },
    onEventUpdated(updatedEvent) {
      const index = this.events.findIndex(ev => ev.id === updatedEvent.id);
      if (index !== -1) {
        this.events.splice(index, 1, { ...this.events[index], ...updatedEvent });
      }
    },
  },
  watch: {
    selectedDate() {
      this.fetchEvents();
    },
  },
  async created() {
    this.loading = true;
    try {
      const [sensors, place] = await Promise.all([
        this.api.fetchSensors(),
        this.api.fetchRTypePlace(this.placeId),
      ]);
      this.sensors = sensors;
      this.placeEntityIds = place.entity_ids;
    } catch {
      this.notifyError('msg.load_error');
      this.loading = false;
      return;
    }
    await this.fetchEvents();
  },
  beforeDestroy() {
    this.abortControl.abort();
  },
};
</script>

<style lang="sass" scoped>
.date-btn
  font-weight: 400 !important
  border-color: rgba(0, 0, 0, 0.23) !important

.control-group
  display: flex
  flex-direction: column
  gap: 4px

.control-group__label
  font-size: 11px
  color: rgba(0, 0, 0, 0.5)

// 横スクロール枠はここ 1 つだけ。種別ごとのマス目表が同時に動き、
// 名前列と見出し（position: sticky）はこの枠に対して左端へ留まる
.timeline-scroll
  overflow-x: auto

.section-header
  position: sticky
  left: 0
  width: fit-content
  height: 24px
  padding-right: 12px
  background: #fff
  font-size: 11px
  font-weight: 700
  color: rgba(0, 0, 0, 0.6)
  // 名前列（TimelineHeatmap の z-index: 1）より前面に置く
  z-index: 2

.rat-sense-notice
  white-space: pre-line

</style>
